import React, { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Paperclip, X, FileText, Loader2, Images } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;
export const fileUrl = (a) => `${API_URL}/api/rfp/files/${a.path}`;

export const AttachmentList = ({ attachments = [], onChange, testId = 'attachments' }) => {
  const [uploading, setUploading] = useState(false);
  const [libOpen, setLibOpen] = useState(false);
  const [libFiles, setLibFiles] = useState(null);
  const inputRef = useRef(null);
  const readOnly = !onChange;

  const openLibrary = async () => {
    setLibOpen(true);
    try {
      const res = await axios.get(`${API_URL}/api/rfp/uploads`, { withCredentials: true });
      setLibFiles(res.data?.files || []);
    } catch { setLibFiles([]); toast.error('Could not load the attachment library'); }
  };

  const pickFromLibrary = (f) => {
    if ((attachments || []).some(a => a.path === f.path)) { toast.info('Already attached'); return; }
    if ((attachments || []).length >= 10) { toast.error('Attachment limit reached (10)'); return; }
    onChange([...(attachments || []), { path: f.path, filename: f.filename, content_type: f.content_type }].slice(0, 10));
    setLibOpen(false);
    toast.success(`"${f.filename}" attached from the library`);
  };

  const upload = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      const added = [];
      for (const f of Array.from(files).slice(0, 5)) {
        const fd = new FormData();
        fd.append('file', f);
        const res = await axios.post(`${API_URL}/api/rfp/uploads`, fd, { withCredentials: true });
        added.push(res.data);
      }
      onChange([...(attachments || []), ...added].slice(0, 10));
      toast.success(`${added.length} attachment${added.length === 1 ? '' : 's'} added`);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); if (inputRef.current) inputRef.current.value = ''; }
  };

  if (readOnly && !(attachments || []).length) return null;
  return (
    <div className="flex flex-wrap gap-2 items-center" data-testid={testId}>
      {(attachments || []).map((a, i) => (a.content_type || '').startsWith('image/') ? (
        <span key={`${a.path}-${i}`} className="relative inline-block" data-testid={`${testId}-img-${i}`}>
          <a href={fileUrl(a)} target="_blank" rel="noreferrer">
            <img src={fileUrl(a)} alt={a.filename} title={a.filename}
              className="h-16 rounded border border-slate-200 object-cover hover:border-[#4ABFCF]" />
          </a>
          {!readOnly && (
            <button onClick={() => onChange(attachments.filter((_, j) => j !== i))}
              className="absolute -top-1.5 -right-1.5 bg-white border border-slate-200 rounded-full p-px text-slate-400 hover:text-red-600"
              title="Remove" data-testid={`${testId}-remove-${i}`}><X className="w-3 h-3" /></button>
          )}
        </span>
      ) : (
        <span key={`${a.path}-${i}`} className="flex items-center gap-1 text-[10px] bg-slate-50 border border-slate-200 rounded-full px-2 py-0.5 text-slate-600"
          data-testid={`${testId}-file-${i}`}>
          <FileText className="w-3 h-3 text-slate-400 shrink-0" />
          <a href={fileUrl(a)} target="_blank" rel="noreferrer" className="hover:underline max-w-[160px] truncate">{a.filename}</a>
          {!readOnly && (
            <button onClick={() => onChange(attachments.filter((_, j) => j !== i))}
              className="text-slate-300 hover:text-red-600" title="Remove" data-testid={`${testId}-remove-${i}`}>
              <X className="w-2.5 h-2.5" />
            </button>
          )}
        </span>
      ))}
      {!readOnly && (
        <>
          <label className="flex items-center gap-1 text-[10px] text-slate-500 border border-dashed border-slate-300 rounded-full px-2 py-0.5 cursor-pointer hover:border-[#4ABFCF] hover:text-[#0F2040] transition-colors"
            data-testid={`${testId}-add`}>
            {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Paperclip className="w-3 h-3" />}
            Attach
            <input ref={inputRef} type="file" multiple className="hidden" disabled={uploading}
              accept=".png,.jpg,.jpeg,.gif,.webp,.pdf,.docx,.xlsx,.pptx,.txt,.csv,.md"
              onChange={e => upload(e.target.files)} data-testid={`${testId}-input`} />
          </label>
          <button onClick={openLibrary} title="Pick from previously uploaded files"
            className="flex items-center gap-1 text-[10px] text-slate-500 border border-dashed border-slate-300 rounded-full px-2 py-0.5 hover:border-[#4ABFCF] hover:text-[#0F2040] transition-colors"
            data-testid={`${testId}-library`}>
            <Images className="w-3 h-3" />Library
          </button>
          <Dialog open={libOpen} onOpenChange={setLibOpen}>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid={`${testId}-library-dialog`}>
              <DialogHeader>
                <DialogTitle className="text-sm">Attachment library — pick a previously uploaded file</DialogTitle>
              </DialogHeader>
              {libFiles === null ? (
                <Loader2 className="w-5 h-5 animate-spin text-[#4ABFCF] mx-auto my-6" />
              ) : libFiles.length === 0 ? (
                <p className="text-xs text-slate-400 text-center py-6" data-testid={`${testId}-library-empty`}>Nothing uploaded yet — use Attach to add the first file.</p>
              ) : (
                <div className="grid grid-cols-4 sm:grid-cols-5 gap-2">
                  {libFiles.map((f, i) => (
                    <button key={f.path} onClick={() => pickFromLibrary(f)}
                      className="border border-slate-200 rounded-md p-1.5 hover:border-[#4ABFCF] transition-colors text-left"
                      title={`${f.filename} — uploaded by ${f.uploaded_by}`} data-testid={`${testId}-lib-item-${i}`}>
                      {(f.content_type || '').startsWith('image/') ? (
                        <img src={fileUrl(f)} alt={f.filename} className="h-16 w-full object-cover rounded" />
                      ) : (
                        <span className="h-16 w-full flex items-center justify-center bg-slate-50 rounded">
                          <FileText className="w-6 h-6 text-slate-300" />
                        </span>
                      )}
                      <p className="text-[9px] text-slate-500 truncate mt-1">{f.filename}</p>
                    </button>
                  ))}
                </div>
              )}
            </DialogContent>
          </Dialog>
        </>
      )}
    </div>
  );
};
